import React, { useState, useEffect } from 'react';
import { Sidebar } from '@/components/Sidebar';
import { SubscriptionCard } from '@/components/SubscriptionCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Filter, CheckCircle, XCircle, AlertCircle, Copy, CopyPlus, Truck, Loader2, Paperclip, Eye, X, CalendarCheck, ScanLine, RotateCcw, QrCode, FileText } from 'lucide-react';
import { maskPhone, maskCpfCnpj } from '@/lib/formatters';
import { validateCPFCNPJ } from '../utils/validation';
import { useCnpjLookup } from '../hooks/useCnpjLookup';

const ESTADOS_BR = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS',
  'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC',
  'SP', 'SE', 'TO'
];

// Função para formatar valor como moeda brasileira
const formatCurrencyInput = (value) => {
  // Remove tudo que não é número
  let numbers = value.replace(/\D/g, '');
  
  // Se vazio, retorna vazio
  if (!numbers) return '';
  
  // Converte para número e divide por 100 para ter os centavos
  let numValue = parseInt(numbers, 10) / 100;
  
  // Formata como moeda brasileira
  return numValue.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const addMonths = (dateStr, months) => {
  const [year, month, day] = dateStr.split('-').map(Number);
  const targetMonth = month - 1 + months;
  const targetYear = year + Math.floor(targetMonth / 12);
  const normalizedMonth = targetMonth % 12;
  const lastDay = new Date(targetYear, normalizedMonth + 1, 0).getDate();
  const clampedDay = Math.min(day, lastDay);
  return `${targetYear}-${String(normalizedMonth + 1).padStart(2, '0')}-${String(clampedDay).padStart(2, '0')}`;
};

// Função para converter valor formatado para número
const parseCurrencyToNumber = (value) => {
  if (!value) return 0;
  // Remove pontos de milhar e troca vírgula por ponto
  let cleaned = value.replace(/\./g, '').replace(',', '.');
  return parseFloat(cleaned) || 0;
};

const ContasPagar = ({ user, onLogout }) => {
  const [contas, setContas] = useState([]);
  const [filteredContas, setFilteredContas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingConta, setEditingConta] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategoria, setFilterCategoria] = useState('all');
  const [filterDescricao, setFilterDescricao] = useState('all');
  const [descricoesDisponiveis, setDescricoesDisponiveis] = useState([]);
  const [buscaDescricao, setBuscaDescricao] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedContas, setSelectedContas] = useState([]);
  const [repetirConta, setRepetirConta] = useState(false);
  const [numRepeticoes, setNumRepeticoes] = useState(1);
  const [documentos, setDocumentos] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [boletoResultado, setBoletoResultado] = useState(null);
  const [processandoBoleto, setProcessandoBoleto] = useState(false);
  const [importandoBoleto, setImportandoBoleto] = useState(false);
  const [showNovoFornecedorDialog, setShowNovoFornecedorDialog] = useState(false);
  const [fornecedorFormData, setFornecedorFormData] = useState({
    nome: '', cnpj_cpf: '', telefone: '', whatsapp: '', email: '',
    endereco: '', cidade: '', uf: '', fornecedor_de: '', site: '', observacoes: ''
  });
  const [cnpjError, setCnpjError] = useState('');
  const [savingFornecedor, setSavingFornecedor] = useState(false);
  const { buscarCNPJ, loadingCNPJ } = useCnpjLookup();
  const [showPagarDialog, setShowPagarDialog] = useState(false);
  const [pagarData, setPagarData] = useState(null);
  const [carregandoPagar, setCarregandoPagar] = useState(false);
  const [contadoresNota, setContadoresNota] = useState([]);
  const [enviarContador, setEnviarContador] = useState(false);
  const [notaUnidade, setNotaUnidade] = useState('');
  const [notaContadorId, setNotaContadorId] = useState('');
  const [notaVinculadaExistente, setNotaVinculadaExistente] = useState(null);

  const [formData, setFormData] = useState({
    descricao: '',
    categoria: '',
    data_emissao: new Date().toISOString().slice(0, 10),
    data_vencimento: new Date().toISOString().slice(0, 10),
    valor: '',
    forma_pagamento: 'PIX',
    codigo_pagamento: '',
    observacoes: '',
    fornecedor_id: '',
    fornecedor_nome: '',
  });

  const company = JSON.parse(localStorage.getItem('company') || '{}');

  useEffect(() => {
    fetchCategorias();
    fetchDescricoes();
    fetchContas();
    fetchFornecedores();
    fetchContadoresNota();
  }, [selectedMonth, filterStatus, filterCategoria, filterDescricao]);

  const fetchFornecedores = async () => {
    if (!company.id) return;
    try {
      const response = await axiosInstance.get(`/fornecedores/${company.id}?status=Ativo`);
      setFornecedores(response.data);
    } catch (error) {
      console.error('Erro ao carregar fornecedores:', error);
    }
  };

  const fetchContadoresNota = async () => {
    if (!company.id) return;
    try {
      const response = await axiosInstance.get(`/contadores/${company.id}`, { params: { ativo: true } });
      setContadoresNota(response.data);
    } catch (error) {
      console.error('Erro ao carregar contadores:', error);
    }
  };

  const fetchNotaVinculada = async (contaId) => {
    if (!company.id || !contaId) {
      setNotaVinculadaExistente(null);
      return;
    }
    try {
      const response = await axiosInstance.get('/notas-fiscais', {
        params: { empresa_id: company.id, conta_id: contaId },
      });
      setNotaVinculadaExistente(response.data?.[0] || null);
    } catch {
      setNotaVinculadaExistente(null);
    }
  };

  const handleFornecedorCnpjChange = async (value) => {
    const formatted = maskCpfCnpj(value);
    setFornecedorFormData(prev => ({ ...prev, cnpj_cpf: formatted }));
    const digits = value.replace(/\D/g, '');
    if (digits.length === 11 || digits.length === 14) {
      if (!validateCPFCNPJ(formatted)) {
        setCnpjError(digits.length === 11 ? 'CPF inválido' : 'CNPJ inválido');
      } else {
        setCnpjError('');
        if (digits.length === 14) {
          const dados = await buscarCNPJ(digits);
          if (dados) {
            setFornecedorFormData(prev => ({
              ...prev,
              nome: dados.razao_social || prev.nome,
              email: dados.email || prev.email,
              telefone: dados.telefone || prev.telefone,
              cidade: dados.municipio || prev.cidade,
              uf: dados.uf || prev.uf,
              endereco: dados.logradouro ? [dados.logradouro, dados.numero].filter(Boolean).join(', ') : prev.endereco,
            }));
          }
        }
      }
    } else {
      setCnpjError('');
    }
  };

  const handleAbrirNovoFornecedor = () => {
    setCnpjError('');
    setFornecedorFormData({ nome: '', cnpj_cpf: '', telefone: '', whatsapp: '', email: '', endereco: '', cidade: '', uf: '', fornecedor_de: '', site: '', observacoes: '' });
    setShowNovoFornecedorDialog(true);
  };

  const handleSalvarNovoFornecedor = async (e) => {
    e.preventDefault();
    if (!fornecedorFormData.nome.trim()) {
      toast.error('Nome/Razão Social é obrigatório');
      return;
    }
    if (fornecedorFormData.cnpj_cpf) {
      const digits = fornecedorFormData.cnpj_cpf.replace(/\D/g, '');
      if ((digits.length === 11 || digits.length === 14) && !validateCPFCNPJ(fornecedorFormData.cnpj_cpf)) {
        toast.error(digits.length === 11 ? 'CPF inválido' : 'CNPJ inválido');
        return;
      }
    }
    try {
      setSavingFornecedor(true);
      const response = await axiosInstance.post('/fornecedores', { empresa_id: company.id, ...fornecedorFormData });
      const novoFornecedor = { id: response.data.id, nome: fornecedorFormData.nome, empresa_id: company.id, ...fornecedorFormData };
      setFornecedores(prev => [...prev, novoFornecedor]);
      setFormData(prev => ({ ...prev, fornecedor_id: novoFornecedor.id, fornecedor_nome: novoFornecedor.nome }));
      setShowNovoFornecedorDialog(false);
      toast.success('Fornecedor cadastrado!');
    } catch (error) {
      toast.error('Erro ao salvar fornecedor');
    } finally {
      setSavingFornecedor(false);
    }
  };

  const fetchCategorias = async () => {
    try {
      const response = await axiosInstance.get(`/contas/categorias${company.id ? `?company_id=${company.id}` : ''}`);
      setCategorias(response.data.pagar || []);
    } catch (error) {
      toast.error('Erro ao carregar categorias');
    }
  };

  const fetchDescricoes = async () => {
    if (!company.id) return;
    try {
      const response = await axiosInstance.get(`/contas/pagar/descricoes?company_id=${company.id}`);
      setDescricoesDisponiveis([...response.data].sort((a, b) => a.localeCompare(b, 'pt-BR')));
    } catch (error) {
      toast.error('Erro ao carregar descrições de contas a pagar');
    }
  };

  const fetchContas = async () => {
    if (!company.id) return;

    try {
      let url = `/contas/pagar?company_id=${company.id}`;
      // Ao filtrar por descrição, mostra todas as ocorrências independente do mês
      if (selectedMonth && filterDescricao === 'all') url += `&mes=${selectedMonth}`;
      if (filterStatus !== 'all') url += `&status=${filterStatus}`;
      if (filterCategoria !== 'all') url += `&categoria=${filterCategoria}`;
      if (filterDescricao !== 'all') url += `&descricao=${encodeURIComponent(filterDescricao)}`;

      const response = await axiosInstance.get(url);
      setContas(response.data);
    } catch (error) {
      toast.error('Erro ao carregar contas a pagar');
    }
  };

  useEffect(() => {
    const termo = buscaDescricao.trim().toLowerCase();
    if (!termo) {
      setFilteredContas(contas);
    } else {
      setFilteredContas(contas.filter((c) => c.descricao?.toLowerCase().includes(termo)));
    }
  }, [contas, buscaDescricao]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = {
        company_id: company.id,
        user_id: (user?.id || user?.user_id),
        tipo: 'PAGAR',
        ...formData,
        valor: parseCurrencyToNumber(formData.valor),
      };

      if (editingConta) {
        // O formulário não tem campo pra linha_digitavel (só é gravada via "Ler Boleto"),
        // então precisa ser reenviada aqui pra não ser zerada pelo PUT (que substitui a conta inteira).
        await axiosInstance.put(`/contas/pagar/${editingConta.id}`, { ...data, linha_digitavel: editingConta.linha_digitavel || null });
        toast.success('Conta atualizada!');
        setShowDialog(false);
        resetForm();
        fetchContas();
      } else {
        const res = await axiosInstance.post('/contas/pagar', data);
        if (repetirConta && numRepeticoes > 0) {
          const repeticoes = Array.from({ length: numRepeticoes }, (_, i) =>
            axiosInstance.post('/contas/pagar', {
              ...data,
              data_emissao: addMonths(data.data_emissao, i + 1),
              data_vencimento: addMonths(data.data_vencimento, i + 1),
            })
          );
          await Promise.all(repeticoes);
          toast.success(`Conta criada com ${numRepeticoes} repetição(ões)! Você pode anexar documentos à conta principal.`);
        } else {
          toast.success('Conta criada! Agora você pode anexar documentos.');
        }
        const novaConta = { ...data, id: res.data.conta_id };
        setEditingConta(novaConta);
        setNotaVinculadaExistente(null);
        fetchDocumentos(res.data.conta_id);
        fetchContas();
      }
    } catch (error) {
      toast.error('Erro ao salvar conta');
    } finally {
      setLoading(false);
    }
  };

  const fetchDocumentos = async (conta_id) => {
    try {
      const res = await axiosInstance.get(`/contas/pagar/${conta_id}/documentos`);
      setDocumentos(res.data);
    } catch {
      setDocumentos([]);
    }
  };

  const handleUploadDoc = async (e) => {
    const file = e.target.files[0];
    if (!file || !editingConta) return;
    if (enviarContador && (!notaUnidade || !notaContadorId)) {
      toast.error('Selecione a Empresa e o Contador para enviar ao Contador, ou desmarque a opção "Enviar ao Contador".');
      e.target.value = '';
      return;
    }
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axiosInstance.post(`/contas/pagar/${editingConta.id}/documentos`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await fetchDocumentos(editingConta.id);
      toast.success('Documento anexado!');

      if (enviarContador && notaUnidade && notaContadorId && editingConta.fornecedor_id) {
        try {
          const fdNota = new FormData();
          fdNota.append('empresa_id', company.id);
          fdNota.append('unidade', notaUnidade);
          fdNota.append('contador_id', notaContadorId);
          fdNota.append('fornecedor_id', editingConta.fornecedor_id);
          fdNota.append('fornecedor_nome', editingConta.fornecedor_nome || '');
          fdNota.append('data_nf', editingConta.data_emissao);
          fdNota.append('valor', editingConta.valor);
          fdNota.append('forma_pagamento', editingConta.forma_pagamento);
          fdNota.append('categoria', editingConta.categoria);
          fdNota.append('conta_pagar_vinculada_id', editingConta.id);
          fdNota.append('arquivo_pdf', file);
          await axiosInstance.post('/notas-fiscais', fdNota, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          toast.success('Documento enviado também para o Contador (Notas de Compra)!');
          fetchNotaVinculada(editingConta.id);
        } catch (errNota) {
          toast.error(errNota.response?.data?.detail || 'Documento anexado, mas falhou o envio ao Contador');
        }
      }
    } catch {
      toast.error('Erro ao anexar documento');
    } finally {
      setUploadingDoc(false);
      e.target.value = '';
    }
  };

  const handleBoletoFileSelect = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file || !editingConta || !company.id) return;
    setProcessandoBoleto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('company_id', company.id);
      const response = await axiosInstance.post('/contas/pagar/upload-boleto', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setBoletoResultado(response.data);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao processar o boleto');
    } finally {
      setProcessandoBoleto(false);
    }
  };

  const handleConfirmarImportarBoleto = async () => {
    if (!editingConta || !boletoResultado) return;
    setImportandoBoleto(true);
    try {
      await axiosInstance.patch(`/contas/pagar/${editingConta.id}/importar-boleto`, {
        linha_digitavel: boletoResultado.linha_digitavel,
        arquivo_base64: boletoResultado.arquivo_base64,
        arquivo_nome: boletoResultado.arquivo_nome,
      });
      setEditingConta((prev) => prev && ({ ...prev, linha_digitavel: boletoResultado.linha_digitavel }));
      await fetchDocumentos(editingConta.id);
      await fetchContas();
      setBoletoResultado(null);
      toast.success('Boleto importado com sucesso!');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao importar o boleto');
    } finally {
      setImportandoBoleto(false);
    }
  };

  const handleDeleteDoc = async (filename) => {
    if (!window.confirm(`Excluir "${filename}"?`)) return;
    try {
      await axiosInstance.delete(`/contas/pagar/${editingConta.id}/documentos/${encodeURIComponent(filename)}`);
      setDocumentos((prev) => prev.filter((d) => d.filename !== filename));
      toast.success('Documento excluído');
    } catch {
      toast.error('Erro ao excluir documento');
    }
  };

  const handleViewDoc = async (filename) => {
    try {
      const res = await axiosInstance.get(
        `/contas/pagar/${editingConta.id}/documentos/${encodeURIComponent(filename)}`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
    } catch {
      toast.error('Erro ao abrir documento');
    }
  };

  const handleAbrirPagar = async (conta) => {
    setCarregandoPagar(true);
    setShowPagarDialog(true);
    try {
      const res = await axiosInstance.get(`/contas/pagar/${conta.id}/boleto`);
      setPagarData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao carregar dados do boleto');
      setShowPagarDialog(false);
    } finally {
      setCarregandoPagar(false);
    }
  };

  const handleViewDocConta = async (conta) => {
    try {
      const res = await axiosInstance.get(`/contas/pagar/${conta.id}/documentos`);
      const docs = res.data;
      if (!docs || docs.length === 0) {
        toast.error('Nenhum documento anexado a esta conta');
        return;
      }
      const fileRes = await axiosInstance.get(
        `/contas/pagar/${conta.id}/documentos/${encodeURIComponent(docs[0].filename)}`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(fileRes.data);
      window.open(url, '_blank');
    } catch {
      toast.error('Erro ao abrir documento');
    }
  };

  const handleEdit = (conta) => {
    setEditingConta(conta);
    setDocumentos([]);
    setBoletoResultado(null);
    setEnviarContador(false);
    setNotaUnidade('');
    setNotaContadorId('');
    fetchDocumentos(conta.id);
    fetchNotaVinculada(conta.id);
    setFormData({
      descricao: conta.descricao,
      categoria: conta.categoria,
      data_emissao: conta.data_emissao,
      data_vencimento: conta.data_vencimento,
      valor: conta.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      forma_pagamento: conta.forma_pagamento,
      codigo_pagamento: conta.codigo_pagamento || '',
      observacoes: conta.observacoes || '',
      fornecedor_id: conta.fornecedor_id || '',
      fornecedor_nome: conta.fornecedor_nome || '',
    });
    setShowDialog(true);
  };

  const handleDuplicate = (conta) => {
    setEditingConta(null);
    setRepetirConta(false);
    setNumRepeticoes(1);
    setFormData({
      descricao: conta.descricao,
      categoria: conta.categoria,
      data_emissao: conta.data_emissao,
      data_vencimento: conta.data_vencimento,
      valor: conta.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      forma_pagamento: conta.forma_pagamento,
      codigo_pagamento: conta.codigo_pagamento || '',
      observacoes: conta.observacoes || '',
      fornecedor_id: conta.fornecedor_id || '',
      fornecedor_nome: conta.fornecedor_nome || '',
    });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente excluir esta conta?')) return;

    try {
      await axiosInstance.delete(`/contas/pagar/${id}`);
      toast.success('Conta excluída!');
      fetchContas();
    } catch (error) {
      toast.error('Erro ao excluir conta');
    }
  };

  const handleMarcarPago = async (conta) => {
    if (!window.confirm('Confirma marcar esta conta como paga?')) return;
    try {
      await axiosInstance.patch(`/contas/pagar/${conta.id}/status`, {
        status: 'PAGO',
        data_pagamento: new Date().toISOString().slice(0, 10),
      });
      toast.success('Conta marcada como paga!');
      fetchContas();
    } catch (error) {
      toast.error('Erro ao marcar como paga');
    }
  };

  const handleReverterPago = async (conta) => {
    if (!window.confirm('Confirma reverter esta conta para PENDENTE? O lançamento financeiro gerado pela quitação será cancelado.')) return;
    try {
      await axiosInstance.patch(`/contas/pagar/${conta.id}/status`, {
        status: 'PENDENTE',
      });
      toast.success('Conta revertida para pendente!');
      fetchContas();
    } catch (error) {
      toast.error('Erro ao reverter conta');
    }
  };

  const handleToggleAgendado = async (conta) => {
    try {
      const res = await axiosInstance.patch(`/contas/pagar/${conta.id}/agendado`);
      setContas(prev => prev.map(c => c.id === conta.id ? { ...c, agendado: res.data.agendado } : c));
    } catch (error) {
      toast.error('Erro ao atualizar agendamento');
    }
  };

  const handleMarcarPagoEmLote = async () => {
    if (selectedContas.length === 0) {
      toast.error('Selecione pelo menos uma conta');
      return;
    }

    if (!window.confirm(`Confirma marcar ${selectedContas.length} conta(s) como paga(s)?`)) return;

    try {
      const promises = selectedContas.map((id) =>
        axiosInstance.patch(`/contas/pagar/${id}/status`, {
          status: 'PAGO',
          data_pagamento: new Date().toISOString().slice(0, 10),
        })
      );

      await Promise.all(promises);
      toast.success(`${selectedContas.length} conta(s) marcada(s) como paga(s)!`);
      setSelectedContas([]);
      fetchContas();
    } catch (error) {
      toast.error('Erro ao marcar contas como pagas');
    }
  };

  const handleExcluirEmLote = async () => {
    if (selectedContas.length === 0) {
      toast.error('Selecione pelo menos uma conta');
      return;
    }

    if (!window.confirm(`Confirma excluir ${selectedContas.length} conta(s)? Esta ação não pode ser desfeita.`)) return;

    try {
      const promises = selectedContas.map((id) => axiosInstance.delete(`/contas/pagar/${id}`));
      await Promise.all(promises);
      toast.success(`${selectedContas.length} conta(s) excluída(s)!`);
      setSelectedContas([]);
      fetchContas();
    } catch (error) {
      toast.error('Erro ao excluir contas');
    }
  };

  const resetForm = () => {
    setFormData({
      descricao: '',
      categoria: '',
      data_emissao: new Date().toISOString().slice(0, 10),
      data_vencimento: new Date().toISOString().slice(0, 10),
      valor: '',
      forma_pagamento: 'PIX',
      codigo_pagamento: '',
      observacoes: '',
      fornecedor_id: '',
      fornecedor_nome: '',
    });
    setEditingConta(null);
    setRepetirConta(false);
    setNumRepeticoes(1);
    setDocumentos([]);
    setBoletoResultado(null);
    setEnviarContador(false);
    setNotaUnidade('');
    setNotaContadorId('');
    setNotaVinculadaExistente(null);
  };

  const getStatusBadge = (status, data_vencimento) => {
    const today = new Date().toISOString().slice(0, 10);
    if (status === 'PENDENTE' && data_vencimento === today) {
      return (
        <Badge className="flex items-center w-fit bg-yellow-500 text-black border-transparent hover:bg-yellow-500/80">
          <AlertCircle className="w-3 h-3 mr-1" />
          HOJE
        </Badge>
      );
    }

    const variants = {
      PENDENTE: 'default',
      PAGO: 'success',
      ATRASADO: 'destructive',
      PARCIAL: 'warning',
    };

    const icons = {
      PENDENTE: <AlertCircle className="w-3 h-3 mr-1" />,
      PAGO: <CheckCircle className="w-3 h-3 mr-1" />,
      ATRASADO: <XCircle className="w-3 h-3 mr-1" />,
      PARCIAL: <AlertCircle className="w-3 h-3 mr-1" />,
    };

    const labels = {
      PENDENTE: 'A vencer',
      PAGO: 'Pago',
      ATRASADO: 'Atrasada',
      PARCIAL: 'Parcial',
    };

    return (
      <Badge variant={variants[status] || 'default'} className="flex items-center w-fit">
        {icons[status]}
        {labels[status] || status}
      </Badge>
    );
  };

  const getValorColorClass = (status) => {
    if (status === 'PAGO') return 'text-green-500';
    if (status === 'ATRASADO') return 'text-red-500';
    return 'text-blue-500';
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const pendentes = filteredContas
        .filter((c) => c.status === 'PENDENTE' || c.status === 'ATRASADO')
        .map((c) => c.id);
      setSelectedContas(pendentes);
    } else {
      setSelectedContas([]);
    }
  };

  const handleSelectConta = (id, checked) => {
    if (checked) {
      setSelectedContas([...selectedContas, id]);
    } else {
      setSelectedContas(selectedContas.filter((cid) => cid !== id));
    }
  };

  const totalPendente = filteredContas
    .filter((c) => c.status === 'PENDENTE' || c.status === 'ATRASADO')
    .reduce((sum, c) => sum + c.valor, 0);

  const totalPago = filteredContas
    .filter((c) => c.status === 'PAGO')
    .reduce((sum, c) => sum + c.valor, 0);

  return (
    <div className="flex min-h-screen bg-zinc-950 text-white">
      <Sidebar user={user} onLogout={onLogout} activePage="contas-pagar" />

      <div className="flex-1 p-8">
        <div className="space-y-6">
          <SubscriptionCard user={user} />

          {/* Header */}
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold">Contas a Pagar</h1>
              <p className="text-zinc-400 mt-1">Gerencie suas contas e compromissos financeiros</p>
            </div>
            <Button
              onClick={() => {
                resetForm();
                setShowDialog(true);
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nova Conta a Pagar
            </Button>
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-sm font-medium text-zinc-400">Pendente</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-500">
                  R$ {totalPendente.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </div>
                <p className="text-xs text-zinc-500 mt-1">
                  {filteredContas.filter((c) => c.status === 'PENDENTE' || c.status === 'ATRASADO').length} conta(s)
                </p>
              </CardContent>
            </Card>

            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-sm font-medium text-zinc-400">Pago</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-500">
                  R$ {totalPago.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </div>
                <p className="text-xs text-zinc-500 mt-1">
                  {filteredContas.filter((c) => c.status === 'PAGO').length} conta(s)
                </p>
              </CardContent>
            </Card>

            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-sm font-medium text-zinc-400">Total</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  R$ {(totalPendente + totalPago).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </div>
                <p className="text-xs text-zinc-500 mt-1">{filteredContas.length} conta(s)</p>
              </CardContent>
            </Card>
          </div>

          {/* Filtros */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="pt-6">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <Label>Mês</Label>
                  <div className="flex gap-2">
                    <Input
                      type="month"
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(e.target.value)}
                      className="bg-zinc-800 border-zinc-700 flex-1"
                    />
                    {selectedMonth && (
                      <Button
                        type="button"
                        variant="outline"
                        className="border-zinc-700 px-3 shrink-0"
                        onClick={() => setSelectedMonth('')}
                        title="Ver todas em aberto"
                      >
                        ×
                      </Button>
                    )}
                  </div>
                </div>

                <div className="flex-1 min-w-[200px]">
                  <Label>Status</Label>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="PENDENTE">A vencer</SelectItem>
                      <SelectItem value="PAGO">Pago</SelectItem>
                      <SelectItem value="ATRASADO">Atrasada</SelectItem>
                      <SelectItem value="PARCIAL">Parcial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex-1 min-w-[200px]">
                  <Label>Categoria</Label>
                  <Select value={filterCategoria} onValueChange={setFilterCategoria}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      {categorias.filter(cat => cat && cat.trim() !== '').map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {cat}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex-1 min-w-[200px]">
                  <Label>Despesa</Label>
                  <Select value={filterDescricao} onValueChange={setFilterDescricao}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      {descricoesDisponiveis.map((desc) => (
                        <SelectItem key={desc} value={desc}>
                          {desc}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex-1 min-w-[200px]">
                  <Label>Buscar por Descrição</Label>
                  <Input
                    type="text"
                    placeholder="Digite parte da descrição..."
                    value={buscaDescricao}
                    onChange={(e) => setBuscaDescricao(e.target.value)}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
              </div>

              {selectedContas.length > 0 && (
                <div className="mt-4 flex items-center gap-4">
                  <span className="text-sm text-zinc-400">
                    {selectedContas.length} conta(s) selecionada(s)
                  </span>
                  <Button onClick={handleMarcarPagoEmLote} size="sm" className="bg-green-600 hover:bg-green-700">
                    Marcar como Pago
                  </Button>
                  <Button onClick={handleExcluirEmLote} size="sm" variant="outline" className="border-red-600 text-red-600 hover:bg-red-600 hover:text-white">
                    <Trash2 className="w-4 h-4 mr-1" />
                    Excluir Selecionadas
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tabela */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="pt-6">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800">
                    <TableHead className="w-[50px]">
                      <Checkbox
                        checked={selectedContas.length > 0}
                        onCheckedChange={handleSelectAll}
                      />
                    </TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead>Categoria</TableHead>
                    <TableHead>Vencimento</TableHead>
                    <TableHead>Valor</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Pagamento</TableHead>
                    <TableHead>Código</TableHead>
                    <TableHead className="text-center">Agend.</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredContas.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center text-zinc-500 py-8">
                        Nenhuma conta encontrada
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredContas.map((conta) => (
                      <TableRow key={conta.id} className={`border-zinc-800 ${conta.agendado ? 'bg-green-500/10' : ''}`}>
                        <TableCell>
                          {(conta.status === 'PENDENTE' || conta.status === 'ATRASADO') && (
                            <Checkbox
                              checked={selectedContas.includes(conta.id)}
                              onCheckedChange={(checked) => handleSelectConta(conta.id, checked)}
                            />
                          )}
                        </TableCell>
                        <TableCell className="font-medium">{conta.descricao}</TableCell>
                        <TableCell>{conta.categoria}</TableCell>
                        <TableCell>
                          {new Date(conta.data_vencimento + 'T00:00:00').toLocaleDateString('pt-BR')}
                        </TableCell>
                        <TableCell className={`font-medium ${getValorColorClass(conta.status)}`}>
                          R$ {conta.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell>{getStatusBadge(conta.status, conta.data_vencimento)}</TableCell>
                        <TableCell>{conta.forma_pagamento}</TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="outline"
                            className={`border-zinc-700 px-2 ${conta.codigo_pagamento ? 'text-zinc-200 hover:bg-zinc-700' : 'text-zinc-600 cursor-default'}`}
                            disabled={!conta.codigo_pagamento}
                            onClick={() => {
                              if (!conta.codigo_pagamento) return;
                              navigator.clipboard.writeText(conta.codigo_pagamento);
                              toast.success('Código copiado!');
                            }}
                            title={conta.codigo_pagamento ? 'Copiar código' : 'Sem código cadastrado'}
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </Button>
                        </TableCell>
                        <TableCell className="text-center">
                          <button
                            onClick={() => handleToggleAgendado(conta)}
                            title={conta.agendado ? 'Agendado no banco (clique para desmarcar)' : 'Marcar como agendado no banco'}
                            className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-zinc-700 transition-colors"
                          >
                            <CalendarCheck className={`w-4 h-4 ${conta.agendado ? 'text-green-500' : 'text-zinc-600'}`} />
                          </button>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            {conta.status !== 'PAGO' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleMarcarPago(conta)}
                                className="border-green-600 text-green-600 hover:bg-green-600 hover:text-white"
                                title="Quitar"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                            )}
                            {conta.status === 'PAGO' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleReverterPago(conta)}
                                className="border-yellow-600 text-yellow-600 hover:bg-yellow-600 hover:text-white"
                                title="Reverter para pendente"
                              >
                                <RotateCcw className="w-4 h-4" />
                              </Button>
                            )}
                            {conta.linha_digitavel && conta.status !== 'PAGO' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleAbrirPagar(conta)}
                                className="border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white"
                                title="Pagar (linha digitável e código de barras)"
                              >
                                <QrCode className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDuplicate(conta)}
                              className="border-zinc-700 text-zinc-400 hover:text-white"
                              title="Duplicar conta"
                            >
                              <CopyPlus className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleViewDocConta(conta)}
                              className="border-zinc-700 text-zinc-400 hover:text-white"
                              title="Visualizar documento"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEdit(conta)}
                              className="border-zinc-700"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDelete(conta.id)}
                              className="border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Dialog de Criar/Editar */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingConta ? 'Editar Conta' : 'Nova Conta a Pagar'}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Descrição *</Label>
              <Input
                required
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                placeholder="Ex: Aluguel do escritório"
                className="bg-zinc-800 border-zinc-700"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Categoria *</Label>
                <Select
                  required
                  value={formData.categoria}
                  onValueChange={(value) => setFormData({ ...formData, categoria: value })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {categorias.filter(cat => cat && cat.trim() !== '').map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Fornecedor</Label>
                <div className="flex gap-2">
                  <Select
                    value={formData.fornecedor_id || "none"}
                    onValueChange={(value) => {
                      const actualValue = value === "none" ? "" : value;
                      const forn = fornecedores.find(f => f.id === actualValue);
                      setFormData({
                        ...formData,
                        fornecedor_id: actualValue,
                        fornecedor_nome: forn?.nome || ''
                      });
                    }}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 flex-1">
                      <SelectValue placeholder="Selecione (opcional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhum</SelectItem>
                      {fornecedores.map((forn) => (
                        <SelectItem key={forn.id} value={forn.id}>
                          {forn.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleAbrirNovoFornecedor}
                    className="border-zinc-700 text-zinc-300 hover:bg-zinc-700 shrink-0"
                    title="Cadastrar novo fornecedor"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Valor *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">R$</span>
                  <Input
                    required
                    type="text"
                    inputMode="numeric"
                    value={formData.valor}
                    onChange={(e) => setFormData({ ...formData, valor: formatCurrencyInput(e.target.value) })}
                    placeholder="0,00"
                    className="bg-zinc-800 border-zinc-700 pl-10"
                  />
                </div>
              </div>

              <div>
                <Label>Forma de Pagamento *</Label>
                <Select
                  required
                  value={formData.forma_pagamento}
                  onValueChange={(value) => setFormData({ ...formData, forma_pagamento: value })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PIX">PIX</SelectItem>
                    <SelectItem value="Boleto">Boleto</SelectItem>
                    <SelectItem value="Cartão">Cartão</SelectItem>
                    <SelectItem value="Dinheiro">Dinheiro</SelectItem>
                    <SelectItem value="Transferência">Transferência</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Código do Boleto / PIX</Label>
              <div className="flex gap-2">
                <Input
                  value={formData.codigo_pagamento}
                  onChange={(e) => setFormData({ ...formData, codigo_pagamento: e.target.value })}
                  placeholder="Cole o código de barras ou chave PIX"
                  className="bg-zinc-800 border-zinc-700 flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  className="border-zinc-700 px-3 shrink-0"
                  disabled={!formData.codigo_pagamento}
                  onClick={() => {
                    navigator.clipboard.writeText(formData.codigo_pagamento);
                    toast.success('Código copiado!');
                  }}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data de Emissão *</Label>
                <Input
                  required
                  type="date"
                  value={formData.data_emissao}
                  onChange={(e) => setFormData({ ...formData, data_emissao: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>

              <div>
                <Label>Data de Vencimento *</Label>
                <Input
                  required
                  type="date"
                  value={formData.data_vencimento}
                  onChange={(e) => setFormData({ ...formData, data_vencimento: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
            </div>

            <div>
              <Label>Observações</Label>
              <Textarea
                value={formData.observacoes}
                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                placeholder="Observações adicionais..."
                className="bg-zinc-800 border-zinc-700"
                rows={3}
              />
            </div>

            {editingConta && (
              <div className="border border-zinc-700 rounded-md p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <Paperclip className="w-4 h-4" />
                    Documentos Anexados
                  </Label>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      className="hidden"
                      onChange={handleUploadDoc}
                      disabled={uploadingDoc}
                    />
                    <span className="inline-flex items-center gap-1 text-sm px-3 py-1 rounded-md border border-zinc-600 text-zinc-300 hover:bg-zinc-700 transition-colors">
                      {uploadingDoc ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                      Anexar
                    </span>
                  </label>
                </div>
                {documentos.length === 0 ? (
                  <p className="text-xs text-zinc-500">Nenhum documento anexado</p>
                ) : (
                  <ul className="space-y-2">
                    {documentos.map((doc) => (
                      <li key={doc.filename} className="flex items-center justify-between bg-zinc-800 rounded px-3 py-2">
                        <span className="text-sm text-zinc-300 truncate flex-1 mr-2">{doc.filename}</span>
                        <div className="flex gap-1 shrink-0">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="border-zinc-600 px-2 h-7"
                            onClick={() => handleViewDoc(doc.filename)}
                            title="Visualizar"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="border-red-700 text-red-500 hover:bg-red-700 hover:text-white px-2 h-7"
                            onClick={() => handleDeleteDoc(doc.filename)}
                            title="Excluir"
                          >
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {editingConta && (
              <div className="border border-zinc-700 rounded-md p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="enviar-contador"
                    checked={enviarContador}
                    onCheckedChange={setEnviarContador}
                    disabled={!editingConta.fornecedor_id || !!notaVinculadaExistente}
                  />
                  <Label htmlFor="enviar-contador" className="cursor-pointer flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Enviar este documento também para o Contador (Notas de Compra)
                  </Label>
                </div>
                {!editingConta.fornecedor_id && (
                  <p className="text-xs text-amber-500">
                    Selecione um Fornecedor nesta conta para poder enviar o documento ao Contador.
                  </p>
                )}
                {notaVinculadaExistente && (
                  <p className="text-xs text-amber-500">
                    Esta conta já tem uma Nota de Compra vinculada (mês de referência {notaVinculadaExistente.mes_referencia}) — não é possível vincular duas vezes.
                  </p>
                )}
                {enviarContador && editingConta.fornecedor_id && !notaVinculadaExistente && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Empresa *</Label>
                      <Select
                        value={notaUnidade}
                        onValueChange={(v) => { setNotaUnidade(v); setNotaContadorId(''); }}
                      >
                        <SelectTrigger className="bg-zinc-800 border-zinc-700">
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="matriz">Matriz</SelectItem>
                          <SelectItem value="filial">Filial</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Contador *</Label>
                      <Select
                        value={notaContadorId}
                        onValueChange={setNotaContadorId}
                        disabled={!notaUnidade}
                      >
                        <SelectTrigger className="bg-zinc-800 border-zinc-700">
                          <SelectValue placeholder={notaUnidade ? 'Selecione' : 'Selecione a empresa primeiro'} />
                        </SelectTrigger>
                        <SelectContent>
                          {contadoresNota.filter((c) => c.empresas_atendidas.includes(notaUnidade)).map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <p className="text-xs text-zinc-500 col-span-2">
                      Use o botão "Anexar" no card "Documentos Anexados" acima para enviar o PDF (DANFE) — ele será salvo aqui e também nas Notas de Compra do contador selecionado.
                    </p>
                  </div>
                )}
              </div>
            )}

            {editingConta && (
              <div className="border border-zinc-700 rounded-md p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <ScanLine className="w-4 h-4" />
                    Ler Boleto (IA)
                  </Label>
                  {!boletoResultado && (
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                        className="hidden"
                        onChange={handleBoletoFileSelect}
                        disabled={processandoBoleto}
                      />
                      <span className="inline-flex items-center gap-1 text-sm px-3 py-1 rounded-md border border-zinc-600 text-zinc-300 hover:bg-zinc-700 transition-colors">
                        {processandoBoleto ? <Loader2 className="w-3 h-3 animate-spin" /> : <ScanLine className="w-3 h-3" />}
                        {processandoBoleto ? 'Lendo...' : 'Ler Boleto'}
                      </span>
                    </label>
                  )}
                </div>

                {!boletoResultado && (
                  <p className="text-xs text-zinc-500">
                    Envie o boleto (PDF ou foto) para importar a linha digitável e anexar o arquivo a esta conta.
                  </p>
                )}

                {boletoResultado?.duplicado && (
                  <div className="bg-zinc-800 rounded-lg p-3 space-y-2 text-sm">
                    <p className="text-yellow-400 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" /> Este boleto já está lançado em outra conta
                    </p>
                    <p className="text-zinc-400">
                      Descrição: <span className="text-zinc-200">{boletoResultado.conta_existente.descricao}</span>
                    </p>
                    <Button type="button" size="sm" variant="outline" className="border-zinc-600" onClick={() => setBoletoResultado(null)}>
                      Fechar
                    </Button>
                  </div>
                )}

                {boletoResultado && !boletoResultado.duplicado && (
                  <div className="bg-zinc-800 rounded-lg p-3 space-y-3 text-sm">
                    {boletoResultado.codigo_barras_imagem_base64 && (
                      <div className="bg-white rounded p-2 flex justify-center">
                        <img
                          src={`data:image/png;base64,${boletoResultado.codigo_barras_imagem_base64}`}
                          alt="Código de barras do boleto"
                          className="max-w-full"
                        />
                      </div>
                    )}
                    <p className="text-zinc-400 text-xs">
                      Confira com os dados já lançados na conta antes de importar (valor e vencimento não são alterados automaticamente):
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <p className="text-zinc-500 text-xs">Fornecedor (boleto)</p>
                        <p className="text-zinc-200">{boletoResultado.fornecedor || '—'}</p>
                      </div>
                      <div>
                        <p className="text-zinc-500 text-xs">Valor (boleto)</p>
                        <p className="text-zinc-200">R$ {Number(boletoResultado.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                      </div>
                      <div>
                        <p className="text-zinc-500 text-xs">Vencimento (boleto)</p>
                        <p className="text-zinc-200">
                          {boletoResultado.data_vencimento ? new Date(boletoResultado.data_vencimento + 'T00:00:00').toLocaleDateString('pt-BR') : '—'}
                        </p>
                      </div>
                      <div>
                        <p className="text-zinc-500 text-xs">Valor / Vencimento nesta conta</p>
                        <p className="text-zinc-200">
                          R$ {formData.valor || '0,00'} — {formData.data_vencimento ? new Date(formData.data_vencimento + 'T00:00:00').toLocaleDateString('pt-BR') : '—'}
                        </p>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Linha Digitável</Label>
                      <Input readOnly value={boletoResultado.linha_digitavel} className="bg-zinc-900 border-zinc-700 text-zinc-400 text-xs mt-1" />
                    </div>
                    <div className="flex gap-2">
                      <Button type="button" size="sm" variant="outline" className="flex-1 border-zinc-600" onClick={() => setBoletoResultado(null)}>
                        Cancelar
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        disabled={importandoBoleto}
                        className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                        onClick={handleConfirmarImportarBoleto}
                      >
                        {importandoBoleto ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Importando...</> : 'Confirmar e Anexar'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {!editingConta && (
              <div className="border border-zinc-700 rounded-md p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="repetir-conta"
                    checked={repetirConta}
                    onCheckedChange={setRepetirConta}
                  />
                  <Label htmlFor="repetir-conta" className="cursor-pointer">
                    Repetir esta conta mensalmente
                  </Label>
                </div>
                {repetirConta && (
                  <div>
                    <Label>Número de repetições</Label>
                    <Input
                      type="number"
                      min={1}
                      max={60}
                      value={numRepeticoes}
                      onChange={(e) => setNumRepeticoes(Math.max(1, parseInt(e.target.value) || 1))}
                      className="bg-zinc-800 border-zinc-700 w-32 mt-1"
                    />
                    <p className="text-xs text-zinc-500 mt-1">
                      Serão criadas mais {numRepeticoes} cópia(s) com vencimento nos meses seguintes, no mesmo dia.
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowDialog(false);
                  resetForm();
                }}
                className="border-zinc-700"
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={loading} className="bg-red-600 hover:bg-red-700">
                {loading ? 'Salvando...' : editingConta ? 'Atualizar' : 'Criar'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal: Novo Fornecedor */}
      <Dialog open={showNovoFornecedorDialog} onOpenChange={setShowNovoFornecedorDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-orange-400" />
              Novo Fornecedor
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSalvarNovoFornecedor} className="space-y-4">
            <div>
              <Label htmlFor="forn_nome">Nome/Razão Social *</Label>
              <Input
                id="forn_nome"
                value={fornecedorFormData.nome}
                onChange={(e) => setFornecedorFormData(prev => ({ ...prev, nome: e.target.value }))}
                className="bg-zinc-800 border-zinc-700"
                placeholder="Nome do fornecedor"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="forn_cnpj" className="flex items-center gap-2">
                  CNPJ/CPF
                  {loadingCNPJ && <Loader2 className="w-3 h-3 animate-spin text-zinc-400" />}
                </Label>
                <Input
                  id="forn_cnpj"
                  value={fornecedorFormData.cnpj_cpf}
                  onChange={(e) => handleFornecedorCnpjChange(e.target.value)}
                  className={`bg-zinc-800 border-zinc-700 ${cnpjError ? 'border-red-500' : ''}`}
                  placeholder="00.000.000/0001-00"
                />
                {cnpjError && (
                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {cnpjError}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="forn_fornecedor_de">Fornecedor de</Label>
                <Input
                  id="forn_fornecedor_de"
                  value={fornecedorFormData.fornecedor_de}
                  onChange={(e) => setFornecedorFormData(prev => ({ ...prev, fornecedor_de: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="Ex: Materiais elétricos, Mão de obra"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="forn_telefone">Telefone</Label>
                <Input
                  id="forn_telefone"
                  value={fornecedorFormData.telefone}
                  onChange={(e) => setFornecedorFormData(prev => ({ ...prev, telefone: maskPhone(e.target.value) }))}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="(00) 0000-0000"
                />
              </div>
              <div>
                <Label htmlFor="forn_whatsapp">WhatsApp</Label>
                <Input
                  id="forn_whatsapp"
                  value={fornecedorFormData.whatsapp}
                  onChange={(e) => setFornecedorFormData(prev => ({ ...prev, whatsapp: maskPhone(e.target.value) }))}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="(00) 00000-0000"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="forn_email">Email</Label>
                <Input
                  id="forn_email"
                  type="email"
                  value={fornecedorFormData.email}
                  onChange={(e) => setFornecedorFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="email@fornecedor.com"
                />
              </div>
              <div>
                <Label htmlFor="forn_site">Site</Label>
                <Input
                  id="forn_site"
                  value={fornecedorFormData.site}
                  onChange={(e) => setFornecedorFormData(prev => ({ ...prev, site: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="www.fornecedor.com.br"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="forn_endereco">Endereço</Label>
              <Input
                id="forn_endereco"
                value={fornecedorFormData.endereco}
                onChange={(e) => setFornecedorFormData(prev => ({ ...prev, endereco: e.target.value }))}
                className="bg-zinc-800 border-zinc-700"
                placeholder="Rua, número, bairro"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="forn_cidade">Cidade</Label>
                <Input
                  id="forn_cidade"
                  value={fornecedorFormData.cidade}
                  onChange={(e) => setFornecedorFormData(prev => ({ ...prev, cidade: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="Cidade"
                />
              </div>
              <div>
                <Label htmlFor="forn_uf">UF</Label>
                <Select value={fornecedorFormData.uf} onValueChange={(value) => setFornecedorFormData(prev => ({ ...prev, uf: value }))}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {ESTADOS_BR.map((uf) => (
                      <SelectItem key={uf} value={uf}>{uf}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="forn_observacoes">Observações</Label>
              <Textarea
                id="forn_observacoes"
                value={fornecedorFormData.observacoes}
                onChange={(e) => setFornecedorFormData(prev => ({ ...prev, observacoes: e.target.value }))}
                className="bg-zinc-800 border-zinc-700"
                placeholder="Observações sobre o fornecedor..."
                rows={3}
              />
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setShowNovoFornecedorDialog(false)} className="border-zinc-700">
                Cancelar
              </Button>
              <Button type="submit" disabled={savingFornecedor} className="bg-orange-600 hover:bg-orange-700">
                {savingFornecedor ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Cadastrar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showPagarDialog} onOpenChange={setShowPagarDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-4xl">
          <DialogHeader>
            <DialogTitle>Pagar Boleto</DialogTitle>
          </DialogHeader>

          {carregandoPagar ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
            </div>
          ) : pagarData && (
            <div className="space-y-4">
              <div>
                <p className="text-sm text-zinc-400">{pagarData.descricao}</p>
                <p className="text-lg font-medium text-red-500">
                  R$ {pagarData.valor?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
                <p className="text-sm text-zinc-400">
                  Vencimento: {new Date(pagarData.data_vencimento + 'T00:00:00').toLocaleDateString('pt-BR')}
                </p>
              </div>

              <div className="bg-white rounded-lg p-3 flex justify-center">
                <img
                  src={`data:image/png;base64,${pagarData.codigo_barras_imagem_base64}`}
                  alt="Código de barras do boleto"
                  className="max-w-full"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-400">Linha Digitável</Label>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={pagarData.linha_digitavel}
                    className="bg-zinc-800 border-zinc-700 text-zinc-200 text-xs font-mono"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="border-zinc-700 shrink-0"
                    onClick={() => {
                      navigator.clipboard.writeText(pagarData.linha_digitavel);
                      toast.success('Linha digitável copiada!');
                    }}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowPagarDialog(false)}
              className="border-zinc-700"
            >
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContasPagar;